import { Box, Tab, Tabs } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import UpcomingSessionTable from './UpcomingSessionTable/UpcomingSessionTable';
import CompletedSessionTable from './CompletedSessionTable/CompletedSessionTable';
import CancelledSessionTable from './CancelledSessionTable/CancelledSessionTable';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { TableSkeleton } from '../../../../../components/Admin/SkeletonLoader/SkeletonLoader';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      aria-label={`${props.label} panel`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const defaultPagination = {
  page: 1,
  page_size: 10,
  total_count: 0,
};

const TeacherSession = () => {
  const { id } = useParams();

  // Get initial session tab value from session storage or default to 0
  const getInitialSessionTabValue = () => {
    const savedTab = sessionStorage.getItem(`teacherSessionTab-${id}`);
    if (savedTab !== null) {
      const tabValue = parseInt(savedTab, 10);
      return isNaN(tabValue) ? 0 : tabValue;
    }
    return 0;
  };

  const [value, setValue] = useState(getInitialSessionTabValue());
  const [upcomingSessionData, setUpcomingSessionData] = useState([]);
  const [completedSessionData, setCompletedSessionData] = useState([]);
  const [cancelledSessionData, setCancelledSessionData] = useState([]);
  const [upcomingPagination, setUpcomingPagination] = useState(defaultPagination);
  const [completedPagination, setCompletedPagination] = useState(defaultPagination);
  const [cancelledPagination, setCancelledPagination] = useState(defaultPagination);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  const [loadingCompleted, setLoadingCompleted] = useState(false);
  const [loadingCancelled, setLoadingCancelled] = useState(false);
  const [hasLoadedUpcoming, setHasLoadedUpcoming] = useState(false);
  const [hasLoadedCompleted, setHasLoadedCompleted] = useState(false);
  const [hasLoadedCancelled, setHasLoadedCancelled] = useState(false);

  const getUpcomingSessionData = useCallback(
    async (page = 1, pageSize = 10) => {
      try {
        setLoadingUpcoming(true);
        const { data } = await axiosInstance.get(
          `/admin/sessions/${id}?status=upcoming&page=${page}&page_size=${pageSize}`
        );
        setUpcomingSessionData(data.results || []);
        setUpcomingPagination({
          page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        setHasLoadedUpcoming(true);
      } catch (error) {
        console.log(error);
        setUpcomingSessionData([]);
        setUpcomingPagination(defaultPagination);
        setHasLoadedUpcoming(true);
      } finally {
        setLoadingUpcoming(false);
      }
    },
    [id]
  );

  const getCompletedSessionData = useCallback(
    async (page = 1, pageSize = 10) => {
      try {
        setLoadingCompleted(true);
        const { data } = await axiosInstance.get(
          `/admin/sessions/${id}?status=completed&page=${page}&page_size=${pageSize}`
        );
        setCompletedSessionData(data.results || []);
        setCompletedPagination({
          page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        setHasLoadedCompleted(true);
      } catch (error) {
        console.log(error);
        setCompletedSessionData([]);
        setCompletedPagination(defaultPagination);
        setHasLoadedCompleted(true);
      } finally {
        setLoadingCompleted(false);
      }
    },
    [id]
  );

  const getCancelledSessionData = useCallback(
    async (page = 1, pageSize = 10) => {
      try {
        setLoadingCancelled(true);
        const { data } = await axiosInstance.get(
          `/admin/sessions/${id}?status=cancelled&page=${page}&page_size=${pageSize}`
        );
        setCancelledSessionData(data.results || []);
        setCancelledPagination({
          page,
          page_size: pageSize,
          total_count: data.count || 0,
        });
        setHasLoadedCancelled(true);
      } catch (error) {
        console.log(error);
        setCancelledSessionData([]);
        setCancelledPagination(defaultPagination);
        setHasLoadedCancelled(true);
      } finally {
        setLoadingCancelled(false);
      }
    },
    [id]
  );

  const handleUpcomingPaginationChange = useCallback(
    (newPage, newPageSize) => {
      getUpcomingSessionData(newPage, newPageSize);
    },
    [getUpcomingSessionData]
  );

  const handleCompletedPaginationChange = useCallback(
    (newPage, newPageSize) => {
      getCompletedSessionData(newPage, newPageSize);
    },
    [getCompletedSessionData]
  );

  const handleCancelledPaginationChange = useCallback(
    (newPage, newPageSize) => {
      getCancelledSessionData(newPage, newPageSize);
    },
    [getCancelledSessionData]
  );

  useEffect(() => {
    if (id) {
      setHasLoadedUpcoming(false);
      setHasLoadedCompleted(false);
      setHasLoadedCancelled(false);
    }
  }, [id]);

  useEffect(() => {
    if (value === 0 && !hasLoadedUpcoming) {
      getUpcomingSessionData(1, 10);
    } else if (value === 1 && !hasLoadedCompleted) {
      getCompletedSessionData(1, 10);
    } else if (value === 2 && !hasLoadedCancelled) {
      getCancelledSessionData(1, 10);
    }
  }, [
    value,
    hasLoadedUpcoming,
    hasLoadedCompleted,
    hasLoadedCancelled,
    getUpcomingSessionData,
    getCompletedSessionData,
    getCancelledSessionData,
  ]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    // Save session tab selection to session storage
    sessionStorage.setItem(`teacherSessionTab-${id}`, newValue.toString());
  };

  return (
    <Box
      data-testid="teacher-session-component"
      className="h-[calc(100vh-260px)] overflow-auto relative bg-[#F9F9F9] rounded-lg"
    >
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Upcoming" sx={{ textTransform: 'none' }} {...a11yProps(0)} />
            <Tab label="Completed" sx={{ textTransform: 'none' }} {...a11yProps(1)} />
            <Tab label="Cancelled" sx={{ textTransform: 'none' }} {...a11yProps(2)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          {loadingUpcoming ? (
            <TableSkeleton />
          ) : (
            <UpcomingSessionTable
              upcomingSessionData={upcomingSessionData}
              pagination={upcomingPagination}
              onPaginationChange={handleUpcomingPaginationChange}
            />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {loadingCompleted ? (
            <TableSkeleton />
          ) : (
            <CompletedSessionTable
              completedSessionData={completedSessionData}
              pagination={completedPagination}
              onPaginationChange={handleCompletedPaginationChange}
            />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {loadingCancelled ? (
            <TableSkeleton />
          ) : (
            <CancelledSessionTable
              cancelledSessionData={cancelledSessionData}
              pagination={cancelledPagination}
              onPaginationChange={handleCancelledPaginationChange}
            />
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default TeacherSession;
